// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  engineApiV2,
  EngineTagTypes,
} from '@shared/data-access/api-base/citec-engine-api';
import { rawApi } from '@shared/data-access/api/engine/raw-api';
import { documentsRepositorySlice } from '@shared/data-access/slices/documents-repository';
import type {
  CreateDocumentMutationParams,
  DeleteDocumentParams,
  Document,
  GetDocumentParams,
  UserDocumentDetailResponse,
  ListDocumentsParams,
  UpdateDocumentMutationParams,
  UserDocumentResponse,
} from '@shared/data-access/types/engine-types';

export const documentsApi = engineApiV2.injectEndpoints({
  endpoints: build => ({
    listDocuments: build.query<Document[], ListDocumentsParams>({
      queryFn: async (_params, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.listDocumentsRaw.initiate(undefined, {
            forceRefetch: api.forced,
          }),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        const response = result.data ?? { documents: [] };

        return { data: response.documents ?? [] };
      },
      onQueryStarted: async (_arg, api) => {
        try {
          const { data } = await api.queryFulfilled;

          api.dispatch(documentsRepositorySlice.actions.upsertDocuments(data));
        } catch (error) {
          console.error('[listDocuments]', error);
        }
      },
      providesTags: result =>
        result?.length
          ? [
              ...result.map(document => ({
                type: EngineTagTypes.Document,
                id: document.id,
              })),
              { type: EngineTagTypes.Document, id: 'LIST' },
            ]
          : [{ type: EngineTagTypes.Document, id: 'LIST' }],
    }),

    fetchDocumentDetail: build.mutation<
      UserDocumentDetailResponse,
      GetDocumentParams
    >({
      queryFn: async (params, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.fetchDocumentDetailRaw.initiate(params),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return { data: result.data! };
      },
    }),

    createDocument: build.mutation<
      UserDocumentResponse,
      CreateDocumentMutationParams
    >({
      queryFn: async (params, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.createDocumentRaw.initiate(params),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return { data: result.data! };
      },
      onQueryStarted: async (_arg, api) => {
        try {
          const { data } = await api.queryFulfilled;

          api.dispatch(documentsRepositorySlice.actions.upsertDocuments([data]));
        } catch (error) {
          console.error('[createDocument]', error);
        }
      },
      invalidatesTags: (result, error) => {
        if (error || !result) {
          return [];
        }

        return [
          { type: EngineTagTypes.Document, id: 'LIST' },
          { type: EngineTagTypes.Document, id: result.id },
        ];
      },
    }),

    updateDocument: build.mutation<
      UserDocumentResponse,
      UpdateDocumentMutationParams
    >({
      queryFn: async (params, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.updateDocumentRaw.initiate(params),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return { data: result.data! };
      },
      onQueryStarted: async (_arg, api) => {
        try {
          const { data } = await api.queryFulfilled;

          api.dispatch(documentsRepositorySlice.actions.upsertDocuments([data]));

          api.dispatch(
            documentsApi.util.updateQueryData(
              'listDocuments',
              undefined,
              documents => {
                const index = documents.findIndex(
                  document => document.id === data.id,
                );

                if (index === -1) {
                  return;
                }

                documents[index] = data;
              },
            ),
          );
        } catch (error) {
          console.error('[updateDocument]', error);
        }
      },
      invalidatesTags: (result, error) => {
        if (error || !result) {
          return [];
        }

        return [];
      },
    }),

    deleteDocument: build.mutation<void, DeleteDocumentParams>({
      queryFn: async (params, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.deleteDocumentRaw.initiate(params),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return { data: null as unknown as void };
      },
      onQueryStarted: async (arg, api) => {
        try {
          await api.queryFulfilled;

          api.dispatch(
            documentsRepositorySlice.actions.removeDocument({
              documentId: arg.document_id,
            }),
          );
        } catch (error) {
          console.error('[deleteDocument]', error);
        }
      },
      invalidatesTags: (_result, error, arg) => {
        if (error) {
          return [];
        }

        return [
          { type: EngineTagTypes.Document, id: arg.document_id },
          { type: EngineTagTypes.Document, id: 'LIST' },
        ];
      },
    }),
  }),
});
